import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { supabase } from "@/integrations/supabase/client";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { DeferredAnalytics } from "@/components/DeferredAnalytics";

type Review = { author: string; quote: string; rating: number };

type AnalyticsSettings = {
  ga4_measurement_id: string | null;
  gsc_verification: string | null;
  bing_verification: string | null;
  clarity_project_id: string | null;
  business_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  business_hours: string | null;
  avg_rating: number | null;
  trips_count: number | null;
  reviews: Review[];
};

async function loadAnalyticsSettings(): Promise<AnalyticsSettings> {
  const empty: AnalyticsSettings = {
    ga4_measurement_id: null, gsc_verification: null, bing_verification: null, clarity_project_id: null,
    business_name: null, contact_phone: null, contact_email: null, business_hours: null,
    avg_rating: null, trips_count: null, reviews: [],
  };
  try {
    const [settingsRes, reviewsRes] = await Promise.all([
      supabase
        .from("settings")
        .select("ga4_measurement_id, gsc_verification, bing_verification, clarity_project_id, business_name, contact_phone, contact_email, business_hours, avg_rating, trips_count")
        .eq("id", 1)
        .maybeSingle(),
      supabase
        .from("testimonials")
        .select("author, quote, rating")
        .eq("active", true)
        .eq("approved", true)
        .order("sort_order", { ascending: true })
        .limit(8),
    ]);
    const data = settingsRes.data;
    return {
      ga4_measurement_id: data?.ga4_measurement_id ?? null,
      gsc_verification: data?.gsc_verification ?? null,
      bing_verification: data?.bing_verification ?? null,
      clarity_project_id: data?.clarity_project_id ?? null,
      business_name: data?.business_name ?? null,
      contact_phone: data?.contact_phone ?? null,
      contact_email: data?.contact_email ?? null,
      business_hours: data?.business_hours ?? null,
      avg_rating: data?.avg_rating ?? null,
      trips_count: data?.trips_count ?? null,
      reviews: (reviewsRes.data ?? []) as Review[],
    };
  } catch {
    return empty;
  }
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  loader: async ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: ["analytics-settings"],
      queryFn: loadAnalyticsSettings,
      staleTime: 5 * 60 * 1000,
    }),
  head: ({ loaderData }) => {
    const a: AnalyticsSettings = (loaderData as AnalyticsSettings | undefined) ?? {
      ga4_measurement_id: null, gsc_verification: null, bing_verification: null, clarity_project_id: null,
      business_name: null, contact_phone: null, contact_email: null, business_hours: null,
      avg_rating: null, trips_count: null, reviews: [],
    };
    const meta: Array<Record<string, string>> = [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Creed Car Rental" },
      { name: "twitter:card", content: "summary_large_image" },
    ];
    if (a.gsc_verification) meta.push({ name: "google-site-verification", content: a.gsc_verification });
    if (a.bing_verification) meta.push({ name: "msvalidate.01", content: a.bing_verification });

    const scripts: Array<Record<string, any>> = [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AutoRental",
          "@id": "https://creedcarental.com/#business",
          name: a.business_name ?? "Creed Car Rental",
          url: "https://creedcarental.com",
          image: "https://creedcarental.com/og-cover.jpg",
          telephone: a.contact_phone ?? "+1 (305) 520-9765",
          email: a.contact_email ?? "Creedcarental@gmail.com",
          priceRange: "$$$",
          address: {
            "@type": "PostalAddress",
            addressLocality: "Miami",
            addressRegion: "FL",
            addressCountry: "US",
          },
          areaServed: [
            { "@type": "City", name: "Miami" },
            { "@type": "City", name: "Miami Beach" },
            { "@type": "City", name: "Fort Lauderdale" },
            { "@type": "AdministrativeArea", name: "Miami-Dade County" },
            { "@type": "AdministrativeArea", name: "Broward County" },
          ],
          openingHours: ["Mo-Sa 09:00-19:00", "Su 10:00-17:00"],
          ...(a.avg_rating && a.trips_count
            ? {
                aggregateRating: {
                  "@type": "AggregateRating",
                  ratingValue: a.avg_rating,
                  reviewCount: a.trips_count,
                  bestRating: 5,
                },
              }
            : {}),
          ...(a.reviews.length
            ? {
                review: a.reviews.map((r) => ({
                  "@type": "Review",
                  reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 },
                  author: { "@type": "Person", name: r.author },
                  reviewBody: r.quote,
                })),
              }
            : {}),
          sameAs: [],
        }),
      },
    ];

    return {
      meta,
      links: [
        { rel: "stylesheet", href: appCss },
        // Warm up the connection to Supabase Storage so car photos load faster.
        { rel: "preconnect", href: "https://bcydrxpqihmkvzfzvobm.supabase.co", crossOrigin: "anonymous" },
        { rel: "dns-prefetch", href: "https://bcydrxpqihmkvzfzvobm.supabase.co" },
      ],
      scripts,
    };
  },

  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const a = Route.useLoaderData() as AnalyticsSettings | undefined;

  return (
    <QueryClientProvider client={queryClient}>
      <PaymentTestModeBanner />
      <DeferredAnalytics
        ga4Id={a?.ga4_measurement_id ?? null}
        clarityId={a?.clarity_project_id ?? "x8fgamuwk0"}
      />
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <main id="main-content">
        <Outlet />
      </main>
    </QueryClientProvider>
  );
}

